import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Filter, Calendar, Clock, BookOpen } from 'lucide-react';
import { INSIGHTS } from '../mock';
import InsightMotif from '../components/InsightMotif';
import ClosingCTA from '../components/ClosingCTA';
import SEO from '../components/SEO';

const CATEGORIES = ['All', 'Playbook', 'Blueprint', 'Handbook', 'Positioning', 'Guide'];

export default function Insights() {
  const [active, setActive] = useState('All');
  const items = useMemo(() => (active === 'All' ? INSIGHTS : INSIGHTS.filter((p) => p.category === active)), [active]);
  const featured = INSIGHTS.find((p) => p.featured) || INSIGHTS[0];
  const rest = items.filter((p) => p.slug !== featured.slug);

  return (
    <div>
      <SEO
        title="Insights — ideas, playbooks and field notes"
        description="DigitalWorks-authored thinking on AI-native transformation, composable commerce and modern retail. Handbooks, blueprints and guides written by the practitioners who ship them."
        canonical="https://digitalworks.co/insights"
      />
      {/* HERO */}
      <section className="pt-16 pb-8">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.24em] uppercase text-[#22A94F] mb-6">Insights</div>
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 lg:col-span-8">
              <h1 className="serif-tight text-[54px] md:text-[96px] xl:text-[116px] leading-[0.96]">
                Ideas, playbooks and<br /><span className="text-[#22A94F]">field notes.</span>
              </h1>
            </div>
            <div className="col-span-12 lg:col-span-4">
              <p className="text-[16px] leading-relaxed text-[#1A1A1A]/75">
                DigitalWorks-authored thinking on AI-native transformation, composable commerce and modern retail. Written by our practitioners, from the work they ship.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-10">
        <div className="dwx">
          <Link to={`/insights/${featured.slug}`} className="group grid grid-cols-12 gap-8 rounded-[28px] bg-[#0F3D22] p-8 md:p-12 items-center lift">
            <div className="col-span-12 md:col-span-5">
              <div className="rounded-[18px] bg-[#FBFAF6]/6 border border-[#FBFAF6]/12 h-64 md:h-72 overflow-hidden text-[#7ED4A0]">
                <InsightMotif variant={featured.motif} tone="#7ED4A0" />
              </div>
            </div>
            <div className="col-span-12 md:col-span-7">
              <div className="mono text-[10px] tracking-[0.24em] uppercase text-[#7ED4A0]">Featured · {featured.category} · {featured.format}</div>
              <h2 className="serif-tight text-[38px] md:text-[54px] leading-[1] text-[#FBFAF6] mt-4">{featured.title}</h2>
              <div className="text-[15px] text-[#FBFAF6]/70 mt-3">{featured.subtitle}</div>
              <p className="text-[15px] leading-relaxed text-[#FBFAF6]/80 mt-6 max-w-xl">{featured.excerpt}</p>
              <div className="mt-6 flex items-center gap-6 text-[11px] mono uppercase text-[#FBFAF6]/55">
                <span className="inline-flex items-center gap-2"><Calendar size={13} />{featured.date}</span>
                <span className="inline-flex items-center gap-2"><Clock size={13} />{featured.readTime}</span>
                <span className="inline-flex items-center gap-2"><BookOpen size={13} />{featured.format}</span>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold text-[#22A94F] group-hover:text-[#7ED4A0] transition-colors">
                Read the piece <ArrowUpRight size={16} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* FILTER + GRID */}
      <section className="py-10">
        <div className="dwx">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 pb-6 border-b border-[#E8E4D9]">
            <div className="flex items-center gap-3 text-[#1A1A1A]/60">
              <Filter size={14} />
              <span className="mono text-[10px] tracking-[0.24em] uppercase">Browse by format</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button key={c} onClick={() => setActive(c)} className={`px-4 py-2 rounded-full text-[12.5px] transition-colors ${active === c ? 'bg-[#1A1A1A] text-[#FBFAF6]' : 'bg-transparent text-[#1A1A1A]/70 border border-[#E8E4D9] hover:border-[#1A1A1A]/30 hover:text-[#1A1A1A]'}`}>{c}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rest.map((p, i) => {
              const styles = [
                { bg: '#F4F2EC', ink: '#1A1A1A', sub: 'text-[#1A1A1A]/70', tone: '#22A94F' },
                { bg: '#1A1A1A', ink: '#FBFAF6', sub: 'text-[#FBFAF6]/70', tone: '#22A94F' },
                { bg: '#F4F2EC', ink: '#1A1A1A', sub: 'text-[#1A1A1A]/70', tone: '#0F3D22' },
                { bg: '#0F3D22', ink: '#FBFAF6', sub: 'text-[#FBFAF6]/75', tone: '#7ED4A0' },
              ];
              const s = styles[i % styles.length];
              return (
                <Link key={p.slug} to={`/insights/${p.slug}`} className="group rounded-[20px] overflow-hidden lift flex flex-col" style={{ backgroundColor: s.bg, color: s.ink }}>
                  <div className="h-48 border-b border-black/5 p-6" style={{ background: s.bg === '#F4F2EC' ? '#FBFAF6' : 'rgba(255,255,255,0.04)' }}>
                    <InsightMotif variant={p.motif} tone={s.tone} />
                  </div>
                  <div className="p-7 flex-1 flex flex-col">
                    <div className="flex items-center justify-between">
                      <span className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: s.tone }}>{p.category} · {p.format}</span>
                      <ArrowUpRight size={16} className="opacity-50 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <h3 className="serif text-[24px] leading-tight mt-4">{p.title}</h3>
                    <div className={`text-[13px] mt-1 ${s.sub}`}>{p.subtitle}</div>
                    <p className={`text-[13px] leading-relaxed mt-4 line-clamp-3 ${s.sub}`}>{p.excerpt}</p>
                    <div className={`mt-auto pt-5 flex items-center gap-4 text-[10.5px] mono uppercase ${s.sub}`}>
                      <span>{p.date}</span><span>{p.readTime}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
